import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { List } from "@refinedev/antd";
import {
  useApiUrl,
  useCreate,
  useCustom,
  useDelete,
  useTranslate,
  useUpdate,
} from "@refinedev/core";
import { Button, Card, Col, Modal, Row, message } from "antd";
import { ExclamationCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { ProductRuleCard } from "./components/ProductRuleCard";
import type {
  IProductRule,
  IProductRuleMetadata,
} from "../../models/product-rule";

/** Well above any realistic rule count; the API pages at 10 by default. */
const RULES_PAGE_SIZE = 500;

export const ProductRulesList: React.FC<{ hideHeader?: boolean }> = ({
  hideHeader,
}) => {
  const _t = useTranslate();
  const apiUrl = useApiUrl();
  const [localRules, setLocalRules] = useState<IProductRule[]>([]);
  const [nextTempId, setNextTempId] = useState(1);

  /**
   * `useCustom` rather than `useList`: simple-rest's getList ignores
   * `meta.query`, so there is no way to pass this endpoint's `page`/`limit`
   * through it, and the response is a `{ data, total, ... }` envelope rather
   * than the bare array getList expects.
   */
  const {
    data,
    isLoading,
    refetch,
  } = useCustom<{ data: IProductRule[]; total: number }>({
    url: `${apiUrl}/product-rules`,
    method: "get",
    config: { query: { page: 1, limit: RULES_PAGE_SIZE } },
    queryOptions: { queryKey: ["product-rules-list"] },
  });

  const { data: metadataData } = useCustom<IProductRuleMetadata>({
    url: `${apiUrl}/product-rules/metadata`,
    method: "get",
    queryOptions: { queryKey: ["product-rules-metadata"] },
  });

  const { mutate: createRule } = useCreate();
  const { mutate: updateRule } = useUpdate();
  const { mutate: deleteRule } = useDelete();

  const savedRules = useMemo<IProductRule[]>(() => {
    const payload = data?.data as unknown;
    if (Array.isArray(payload)) return payload as IProductRule[];
    if (payload && typeof payload === "object" && "data" in payload) {
      const inner = (payload as { data: unknown }).data;
      if (Array.isArray(inner)) return inner as IProductRule[];
    }
    return [];
  }, [data]);

  const allRules = useMemo(
    () => [...localRules, ...savedRules],
    [localRules, savedRules],
  );

  const handleCreateNew = () => {
    setLocalRules((previous) => [
      {
        id: `temp-${nextTempId}`,
        name: _t("product-rules.fields.newRule", "New rule"),
        description: "",
        message: "",
        isActive: true,
        conditions: [],
        productIds: [],
        _isNew: true,
      },
      ...previous,
    ]);
    setNextTempId((previous) => previous + 1);
  };

  const handleUpdate = (
    id: string | number,
    values: Partial<IProductRule>,
  ) => {
    const idString = String(id);
    const isNew = idString.startsWith("temp-");

    // Strip anything the API returns but does not accept on write.
    const {
      _isNew,
      products,
      tenantId,
      createdAt,
      updatedAt,
      id: _id,
      ...payload
    } = values as IProductRule & Record<string, unknown>;

    if (isNew) {
      createRule(
        { resource: "product-rules", values: payload },
        {
          onSuccess: () => {
            message.success(
              _t("product-rules.messages.createSuccess", "Rule created"),
            );
            setLocalRules((previous) =>
              previous.filter((rule) => String(rule.id) !== idString),
            );
            refetch();
          },
          onError: () => {
            message.error(
              _t("product-rules.messages.createError", "Could not create the rule"),
            );
          },
        },
      );
      return;
    }

    updateRule(
      { resource: "product-rules", id: idString, values: payload },
      {
        onSuccess: () => {
          message.success(
            _t("product-rules.messages.updateSuccess", "Rule updated"),
          );
          refetch();
        },
        onError: () => {
          message.error(
            _t("product-rules.messages.updateError", "Could not update the rule"),
          );
        },
      },
    );
  };

  const handleDelete = (id: string | number) => {
    const idString = String(id);
    const isNew = idString.startsWith("temp-");
    const target = allRules.find((rule) => String(rule.id) === idString);
    const name = target?.name ?? _t("product-rules.fields.newRule", "New rule");

    Modal.confirm({
      title: _t("product-rules.actions.delete", "Delete rule"),
      icon: <ExclamationCircleOutlined />,
      content: _t(
        "product-rules.messages.deleteConfirm",
        `Delete the rule "${name}"? This cannot be undone.`,
      ).replace("{{name}}", name),
      okText: _t("buttons.delete", "Delete"),
      okType: "danger",
      cancelText: _t("buttons.cancel", "Cancel"),
      onOk: () => {
        if (isNew) {
          setLocalRules((previous) =>
            previous.filter((rule) => String(rule.id) !== idString),
          );
          return;
        }
        deleteRule(
          { resource: "product-rules", id: idString },
          {
            onSuccess: () => {
              message.success(
                _t("product-rules.messages.deleteSuccess", "Rule deleted"),
              );
              refetch();
            },
            onError: () => {
              message.error(
                _t("product-rules.messages.deleteError", "Could not delete the rule"),
              );
            },
          },
        );
      },
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      <List
        title={hideHeader ? false : _t("product-rules.title", "Product rules")}
        breadcrumb={hideHeader ? false : undefined}
        headerButtons={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateNew}>
            {_t("product-rules.actions.create", "Create rule")}
          </Button>
        }
      >
        <Row gutter={[16, 16]} className="w-full">
          {isLoading ? (
            <Col xs={24}>
              <Card loading />
            </Col>
          ) : allRules.length === 0 ? (
            <Col xs={24}>
              <Card>
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  {_t(
                    "product-rules.noRules",
                    "No product rules yet. Every product is available to everyone.",
                  )}
                </div>
              </Card>
            </Col>
          ) : (
            allRules.map((rule) => (
              <Col key={rule.id} xs={24}>
                <motion.div
                  layout
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                  <ProductRuleCard
                    rule={rule}
                    metadata={metadataData?.data}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                  />
                </motion.div>
              </Col>
            ))
          )}
        </Row>
      </List>
    </div>
  );
};
